import { currency } from "const/constants";
import { FaFileAlt, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord } from "react-icons/fa";

export function formatMoney(value, dollar = currency) {
  let number = Number(value);

  const isNegative = number < 0;
  const absoluteValue = Math.abs(number);

  // Format number with commas
  const formattedNumber = absoluteValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Construct final money string
  return isNegative ? `-${dollar}${formattedNumber}` : `${dollar}${formattedNumber}`;
}

/**
 * A provider-generated invoice line whose amount has been edited down to zero after the invoice
 * was raised (see ProviderAdmissionService.syncInvoiceItems) should read as if it were never
 * generated — hide it everywhere the invoice is rendered. Only applies to provider-linked lines
 * (providerAdmissionTermId set); manually-added lines are never hidden.
 */
export function isHiddenZeroProviderInvoiceItem(item) {
  if (!item?.providerAdmissionTermId) return false;
  const amount = item?.total ?? item?.price ?? 0;
  return Number(amount) <= 0;
}

export const getStatus = (status) => {
  const statusString = typeof status === "string" ? status : "";
  switch (statusString) {
    case "PENDING":
      return "Pending";
    case "IN_PROGRESS":
      return "In Progress";
    case "HOLD":
      return "On Hold";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status;
  }
};

export const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html ?? "", "text/html");
  return doc.body.textContent || "";
};

export const highlightHtml = (html, search) => {
  if (!search) return html;

  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");

  return html.replace(regex, "<mark>$1</mark>");
};

// Presigned S3 URLs carry a signature query string; strip it so the name and
// extension come from the object key, not "file.jpeg?X-Amz-Algorithm=...".
export const stripUrlParams = (filePath) => (filePath || "").split(/[?#]/)[0];

export const getFileIcon = (filePath) => {
  if (!filePath) return <FaFileAlt className="text-app-text-secondary" />;
  const extension = stripUrlParams(filePath).split(".").pop()?.toLowerCase();
  switch (extension) {
    case "pdf":
      return <FaFilePdf className="text-red-500" />;
    case "doc":
    case "docx":
      return <FaFileWord className="text-blue-500" />;
    case "xls":
    case "xlsx":
      return <FaFileExcel className="text-green-500" />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return <FaFileImage className="text-purple-500" />;
    default:
      return <FaFileAlt className="text-app-text-secondary" />;
  }
};
export const getOriginalFileName = (filePath) => {
  const fileName = decodeURIComponent(stripUrlParams(filePath).split("/").pop() || "");
  // Stored files are often prefixed with a UUID or long token, then "_".
  // Example: "<uuid>_COE_Payment_Receipt.pdf" -> "COE_Payment_Receipt.pdf"
  const [prefix, ...rest] = fileName.split("_");
  const isUuidPrefix = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    prefix || ""
  );
  const isLongTokenPrefix = /^[a-z0-9-]{16,}$/i.test(prefix || "");

  if ((isUuidPrefix || isLongTokenPrefix) && rest.length > 0) {
    return rest.join("_");
  }
  return fileName;
};
